/**
 * API Monitor Utility
 * Tracks and logs API calls for debugging performance issues
 */

class ApiMonitor {
  constructor() {
    this.calls = new Map();
    this.startTime = Date.now();
    this.maxCalls = 100; // Keep only last 100 calls
  }

  /**
   * Log an API call
   * @param {string} url - API endpoint
   * @param {string} method - HTTP method
   * @param {number} duration - Call duration in ms
   * @param {boolean} success - Whether call was successful
   */
  logCall(url, method = 'GET', duration = 0, success = true) {
    const timestamp = Date.now();
    const call = {
      url,
      method,
      duration,
      success,
      timestamp,
      timeString: new Date(timestamp).toLocaleTimeString()
    };

    // Store call
    this.calls.set(timestamp, call);

    // Keep only recent calls
    if (this.calls.size > this.maxCalls) {
      const oldestKey = Math.min(...this.calls.keys());
      this.calls.delete(oldestKey);
    }

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      const status = success ? '✅' : '❌';
      const durationStr = duration > 1000 ? `${(duration/1000).toFixed(1)}s` : `${duration}ms`;
      console.log(`${status} ${method} ${url} - ${durationStr}`);
    }
  }

  /**
   * Get call statistics
   */
  getStats() {
    const calls = Array.from(this.calls.values());
    const totalCalls = calls.length;
    const successfulCalls = calls.filter(c => c.success).length;
    const failedCalls = totalCalls - successfulCalls;
    const avgDuration = calls.reduce((sum, c) => sum + c.duration, 0) / totalCalls || 0;
    
    // Group by URL
    const urlStats = {};
    calls.forEach(call => {
      if (!urlStats[call.url]) {
        urlStats[call.url] = { count: 0, totalDuration: 0, errors: 0 };
      }
      urlStats[call.url].count++;
      urlStats[call.url].totalDuration += call.duration;
      if (!call.success) {
        urlStats[call.url].errors++;
      }
    });

    // Calculate averages per URL
    Object.keys(urlStats).forEach(url => {
      const stats = urlStats[url];
      stats.avgDuration = stats.totalDuration / stats.count;
    });

    return {
      totalCalls,
      successfulCalls,
      failedCalls,
      avgDuration: Math.round(avgDuration),
      urlStats,
      uptime: Date.now() - this.startTime,
      recentCalls: calls.slice(-10).reverse() // Last 10 calls
    };
  }

  /**
   * Get calls for a specific URL
   */
  getCallsForUrl(url) {
    return Array.from(this.calls.values())
      .filter(call => call.url === url)
      .sort((a, b) => b.timestamp - a.timestamp);
  }

  /**
   * Clear all logs
   */
  clear() {
    this.calls.clear();
    this.startTime = Date.now();
  }

  /**
   * Get problematic URLs (high frequency or slow)
   */
  getProblematicUrls() {
    const stats = this.getStats();
    const problematic = [];

    Object.entries(stats.urlStats).forEach(([url, urlStats]) => {
      if (urlStats.count > 10) { // More than 10 calls
        problematic.push({
          url,
          ...urlStats,
          issue: 'High frequency'
        });
      } else if (urlStats.avgDuration > 2000) { // Slower than 2s
        problematic.push({
          url,
          ...urlStats,
          issue: 'Slow response'
        });
      }
    });

    return problematic.sort((a, b) => b.count - a.count);
  }
}

// Create singleton instance
const apiMonitor = new ApiMonitor();

export default apiMonitor;








